/**
 * UserManagement.tsx — Super Admin user management page.
 *
 * Features:
 *  - Paginated user table with filters (role, status, search)
 *  - Action menu: block/unblock, edit, change role, impersonate, delete
 *  - Create User dialog with auto-generated temp password
 */

import React, { useCallback, useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockResetIcon from '@mui/icons-material/LockReset';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';

import axios from 'axios';
import API_BASE from '../../config/api';
import { useAuth } from '../../context/AuthContext';

// ── Types ─────────────────────────────────────────────────────────────────────

interface User {
  id: string;
  email: string;
  full_name: string;
  role: string;
  customer_id: string | null;
  is_active: boolean;
  is_blocked: boolean;
  is_verified: boolean;
  billing_enabled: boolean;
  last_login_at: string | null;
  created_at: string;
}

// ── Role chip ─────────────────────────────────────────────────────────────────

function RoleChip({ role }: { role: string }) {
  const color = role === 'super_admin' ? 'error' : role === 'engineer' ? 'warning' : 'default';
  const label = role === 'customer' ? 'Partner' : role.replace(/_/g, ' ');
  return (
    <Chip
      label={label}
      size="small"
      color={color as any}
      sx={{ textTransform: 'capitalize' }}
    />
  );
}

// ── Status chip ───────────────────────────────────────────────────────────────

function StatusChip({ user }: { user: User }) {
  if (user.is_blocked) return <Chip label="Blocked" size="small" color="error" />;
  if (!user.is_verified) return <Chip label="Unverified" size="small" color="warning" />;
  if (!user.is_active)  return <Chip label="Inactive" size="small" />;
  return <Chip label="Active" size="small" color="success" />;
}

// ── Row action menu ───────────────────────────────────────────────────────────

function RowMenu({
  user,
  isSuperAdmin,
  onBlock,
  onUnblock,
  onImpersonate,
  onChangeRole,
  onDeactivate,
  onReactivate,
  onResetPassword,
  onToggleBilling,
}: {
  user: User;
  isSuperAdmin: boolean;
  onBlock: () => void;
  onUnblock: () => void;
  onImpersonate: () => void;
  onChangeRole: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
  onResetPassword: () => void;
  onToggleBilling: () => void;
}) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const close = (fn?: () => void) => { setAnchor(null); fn?.(); };

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {user.is_blocked
          ? <MenuItem onClick={() => close(onUnblock)}>Unblock</MenuItem>
          : <MenuItem onClick={() => close(onBlock)}>Block</MenuItem>}
        <MenuItem onClick={() => close(onChangeRole)}>Change Role</MenuItem>
        <MenuItem onClick={() => close(onResetPassword)}>
          <LockResetIcon fontSize="small" sx={{ mr: 1 }} /> Reset Password
        </MenuItem>
        {user.is_active
          ? <MenuItem onClick={() => close(onDeactivate)} sx={{ color: 'error.main' }}>Suspend</MenuItem>
          : <MenuItem onClick={() => close(onReactivate)} sx={{ color: 'success.main' }}>Reactivate</MenuItem>}
        {/* Super-admin only actions */}
        {isSuperAdmin && (
          <MenuItem onClick={() => close(onImpersonate)}>
            <SwitchAccountIcon fontSize="small" sx={{ mr: 1 }} /> Login as Partner
          </MenuItem>
        )}
        {isSuperAdmin && user.role === 'admin' && (
          <MenuItem onClick={() => close(onToggleBilling)}>
            {user.billing_enabled ? 'Revoke Billing Access' : 'Grant Billing Access'}
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

// ── Reset password dialog ─────────────────────────────────────────────────────

function ResetPasswordDialog({
  open,
  user,
  onClose,
}: {
  open: boolean;
  user: User | null;
  onClose: () => void;
}) {
  const [step, setStep]           = useState<'confirm' | 'loading' | 'done'>('confirm');
  const [tempPassword, setTempPassword] = useState('');
  const [emailSentTo, setEmailSentTo]   = useState('');
  const [error, setError]         = useState('');

  function reset() { setStep('confirm'); setTempPassword(''); setEmailSentTo(''); setError(''); }

  async function doReset() {
    if (!user) return;
    setStep('loading');
    try {
      const { data } = await axios.post(`${API_BASE}/admin/users/${user.id}/reset-password`);
      setTempPassword(data.temp_password);
      setEmailSentTo(data.email_sent_to ?? user.email);
      setStep('done');
    } catch {
      setError('Failed to reset password.');
      setStep('confirm');
    }
  }

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} maxWidth="xs" fullWidth>
      <DialogTitle>Reset Password — {user?.email}</DialogTitle>
      <DialogContent>
        {step === 'confirm' && (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography variant="body2">
              A new temporary password will be generated and emailed to <strong>{user?.email}</strong>.
              All active sessions will be revoked immediately.
            </Typography>
          </>
        )}
        {step === 'loading' && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" mt={2}>Resetting password…</Typography>
          </Box>
        )}
        {step === 'done' && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Password reset. Email sent to {emailSentTo}.
            </Alert>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Temporary password (shown once):
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(0,180,216,0.08)', borderRadius: 1, p: 1.5 }}>
              <Typography variant="body1" sx={{ fontFamily: 'monospace', flex: 1, fontWeight: 700, color: 'primary.main' }}>
                {tempPassword}
              </Typography>
              <Tooltip title="Copy password">
                <IconButton size="small" onClick={() => navigator.clipboard.writeText(tempPassword)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }}>
          {step === 'done' ? 'Done' : 'Cancel'}
        </Button>
        {step === 'confirm' && (
          <Button variant="contained" color="warning" onClick={doReset}>
            Reset Password
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── Create user dialog ────────────────────────────────────────────────────────

function CreateUserDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail]         = useState('');
  const [fullName, setFullName]   = useState('');
  const [role, setRole]           = useState('customer');
  const [customerId, setCustomerId] = useState('');
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState<{ temp_password: string } | null>(null);
  const [error, setError]         = useState('');

  function reset() {
    setEmail(''); setFullName(''); setRole('customer'); setCustomerId('');
    setResult(null); setError('');
  }

  async function handleCreate() {
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API_BASE}/admin/users`, {
        email: email.trim().toLowerCase(),
        full_name: fullName.trim(),
        role,
        customer_id: customerId.trim() || undefined,
      });
      setResult(data);
      onCreated();
    } catch (err: any) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not create user.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} maxWidth="xs" fullWidth>
      <DialogTitle>Create New User</DialogTitle>
      <DialogContent>
        {result ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>User created successfully.</Alert>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Temporary password (share securely — shown once):
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'rgba(0,180,216,0.08)',
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Typography variant="body1" sx={{ fontFamily: 'monospace', flex: 1 }}>
                {result.temp_password}
              </Typography>
              <Tooltip title="Copy">
                <IconButton size="small" onClick={() => navigator.clipboard.writeText(result.temp_password)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ) : (
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              label="Full name"
              fullWidth
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select value={role} label="Role" onChange={(e) => setRole(e.target.value)}>
                <MenuItem value="customer">Partner</MenuItem>
                <MenuItem value="engineer">Engineer</MenuItem>
                <MenuItem value="super_admin">Super Admin</MenuItem>
              </Select>
            </FormControl>
            {role === 'customer' && (
              <TextField
                label="Partner ID (optional)"
                fullWidth
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }}>
          {result ? 'Done' : 'Cancel'}
        </Button>
        {!result && (
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={loading || !email || !fullName}
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : 'Create'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── Change role dialog ────────────────────────────────────────────────────────

function ChangeRoleDialog({
  open,
  user,
  onClose,
  onChanged,
}: {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [role, setRole] = useState(user?.role ?? 'customer');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => { if (user) setRole(user.role); }, [user]);

  async function handleSave() {
    if (!user) return;
    setLoading(true); setError('');
    try {
      await axios.patch(`${API_BASE}/admin/users/${user.id}/role`, { role });
      onChanged();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail ?? 'Could not change role.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Change Role — {user?.email}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel>New Role</InputLabel>
          <Select value={role} label="New Role" onChange={(e) => setRole(e.target.value)}>
            <MenuItem value="customer">Partner</MenuItem>
            <MenuItem value="engineer">Engineer</MenuItem>
            <MenuItem value="super_admin">Super Admin</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}>
          {loading ? <CircularProgress size={18} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function UserManagement() {
  const { user: authUser, token, startImpersonation } = useAuth();
  const isSuperAdmin = authUser?.role === 'super_admin';

  const [users, setUsers]           = useState<User[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [search, setSearch]         = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [changeRoleUser, setChangeRoleUser]   = useState<User | null>(null);
  const [resetPwdUser, setResetPwdUser]       = useState<User | null>(null);
  const [actionMsg, setActionMsg]             = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/admin/users`);
      setUsers(data.users ?? data);
    } catch {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  async function blockUser(user: User) {
    try {
      await axios.post(`${API_BASE}/admin/users/${user.id}/block`, { reason: 'Blocked by admin' });
      setActionMsg(`${user.email} blocked.`);
      fetchUsers();
    } catch { setActionMsg('Action failed.'); }
  }

  async function unblockUser(user: User) {
    try {
      await axios.post(`${API_BASE}/admin/users/${user.id}/unblock`);
      setActionMsg(`${user.email} unblocked.`);
      fetchUsers();
    } catch { setActionMsg('Action failed.'); }
  }

  async function impersonate(targetUser: User) {
    if (!authUser || !token) return;
    try {
      const { data } = await axios.post(`${API_BASE}/admin/users/${targetUser.id}/impersonate`);
      const targetAuthUser = {
        id:                    targetUser.id,
        email:                 targetUser.email,
        full_name:             targetUser.full_name,
        role:                  targetUser.role as any,
        customer_id:           targetUser.customer_id,
        is_verified:           targetUser.is_verified,
        is_active:             targetUser.is_active,
        last_login_at:         targetUser.last_login_at,
        account_status:        null,
        paused_reason:         null,
        force_password_change: false,
        billing_enabled:       false,
        impersonated_by:       authUser.id,
        impersonated_by_email: authUser.email,
      };
      startImpersonation(data.access_token, targetAuthUser, token, authUser);
      setActionMsg(`Now impersonating ${targetUser.email} — see banner at top.`);
    } catch { setActionMsg('Impersonation failed.'); }
  }

  async function toggleBilling(targetUser: User) {
    try {
      const { data } = await axios.patch(`${API_BASE}/admin/users/${targetUser.id}/billing-access`, {
        billing_enabled: !targetUser.billing_enabled,
      });
      setActionMsg(data.message);
      fetchUsers();
    } catch { setActionMsg('Failed to update billing access.'); }
  }

  async function deactivateUser(user: User) {
    if (!window.confirm(`Deactivate ${user.email}? They will be signed out immediately.`)) return;
    try {
      await axios.delete(`${API_BASE}/admin/users/${user.id}`);
      setActionMsg(`${user.email} deactivated.`);
      fetchUsers();
    } catch { setActionMsg('Action failed.'); }
  }

  async function reactivateUser(user: User) {
    try {
      await axios.post(`${API_BASE}/admin/users/${user.id}/activate`);
      setActionMsg(`${user.email} reactivated.`);
      fetchUsers();
    } catch { setActionMsg('Action failed.'); }
  }

  const filtered = users.filter((u) => {
    const matchSearch = !search ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      u.full_name.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    const matchStatus =
      statusFilter === 'all' ? true :
      statusFilter === 'blocked' ? u.is_blocked :
      statusFilter === 'inactive' ? !u.is_active :
      statusFilter === 'unverified' ? !u.is_verified :
      (u.is_active && !u.is_blocked && u.is_verified);
    return matchSearch && matchRole && matchStatus;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" /> User Management
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Create User
        </Button>
      </Box>

      {actionMsg && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setActionMsg('')}>{actionMsg}</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
        <TextField
          placeholder="Search by name or email…"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Role</InputLabel>
          <Select value={roleFilter} label="Role" onChange={(e) => setRoleFilter(e.target.value)}>
            <MenuItem value="all">All Roles</MenuItem>
            <MenuItem value="customer">Partner</MenuItem>
            <MenuItem value="engineer">Engineer</MenuItem>
            <MenuItem value="super_admin">Super Admin</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="blocked">Blocked</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="unverified">Unverified</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Billing</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{u.full_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                  </TableCell>
                  <TableCell><RoleChip role={u.role} /></TableCell>
                  <TableCell><StatusChip user={u} /></TableCell>
                  <TableCell>
                    {u.billing_enabled
                      ? <Chip label="Billing" size="small" color="primary" variant="outlined" />
                      : null}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(u.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <RowMenu
                      user={u}
                      isSuperAdmin={isSuperAdmin}
                      onBlock={() => blockUser(u)}
                      onUnblock={() => unblockUser(u)}
                      onImpersonate={() => impersonate(u)}
                      onChangeRole={() => setChangeRoleUser(u)}
                      onDeactivate={() => deactivateUser(u)}
                      onReactivate={() => reactivateUser(u)}
                      onResetPassword={() => setResetPwdUser(u)}
                      onToggleBilling={() => toggleBilling(u)}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" variant="body2" py={3}>
                      No users found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CreateUserDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={fetchUsers}
      />
      <ChangeRoleDialog
        open={Boolean(changeRoleUser)}
        user={changeRoleUser}
        onClose={() => setChangeRoleUser(null)}
        onChanged={fetchUsers}
      />
      <ResetPasswordDialog
        open={Boolean(resetPwdUser)}
        user={resetPwdUser}
        onClose={() => setResetPwdUser(null)}
      />
    </Box>
  );
}
